"use client";

import { useGetDietAddons } from "@repo/api/catalog/api/diet-addon-group";
import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import { useTranslations } from "next-intl";
import { type ReactNode, useMemo, useState } from "react";
import { findAddonCategoryName } from "@/features/addons/lib/addon-gtm-item";
import type { SubItemsPricing } from "../types/sub-Items-pricing.type";
import { DietAddonModal } from "./diet-addon-modal";
import { DietAddonsCarousel } from "./diet-addons-carousel";
import { DietAddonsSkeleton } from "./diet-addons-skeleton";
import { DietAddonsSummaryDialog } from "./diet-addons-summary-dialog";

type DietAddonsSectionProps = {
  dietId: string;
  // Diet delivery days chosen in the calendar above; the addon modal can only
  // attach addons to these days.
  dietDates?: Date[];
  // Quote of the addons already committed to the position, forwarded to the
  // summary dialog so its rows show charged amounts.
  subItemsPricing?: SubItemsPricing | null;
  cateringName?: string;
};

export const DietAddonsSection = ({
  dietId,
  dietDates,
  subItemsPricing,
  cateringName,
}: DietAddonsSectionProps) => {
  const t = useTranslations();
  const { data, isPending, isError } = useGetDietAddons(dietId, undefined, {
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      select: (response) => response.data,
    },
  });
  const [selectedAddon, setSelectedAddon] =
    useState<AddonDietAddonsRead | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const selectedAddonCategoryName = useMemo(
    () =>
      selectedAddon
        ? findAddonCategoryName(data ?? [], selectedAddon.id)
        : undefined,
    [data, selectedAddon],
  );

  const hasAddons = data?.some((category) => category.addons.length > 0);
  // Hide the whole section when there is nothing to show and no error/loading.
  if (!(isPending || isError || hasAddons)) {
    return null;
  }

  return (
    <section className="w-full min-w-0 space-y-4" id="cart-addons">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-medium text-2xl">
            {t.rich("cart.configuration.addons.title", {
              light: (chunks: ReactNode) => (
                <span className="font-normal text-brand-grey5">{chunks}</span>
              ),
            })}
          </h2>
          <button
            className="shrink-0 font-medium text-brand-green2-300 text-sm underline underline-offset-4"
            onClick={() => setSummaryOpen(true)}
            type="button"
          >
            {/* The design shortens the label to "Zarządzaj" on mobile, where the
                full one would push the heading out of the row. */}
            <span className="md:hidden">
              {t("cart.configuration.addons.manageShort")}
            </span>
            <span className="max-md:hidden">
              {t("cart.configuration.addons.manage")}
            </span>
          </button>
        </div>
        <p className="text-brand-grey5 text-sm">
          {t("cart.configuration.addons.subtitle")}
        </p>
      </div>

      <div className="rounded-2xl bg-brand-grey1 p-4 md:p-6 md:pt-4">
        {isPending && <DietAddonsSkeleton />}

        {isError && (
          <p className="text-brand-grey5 text-sm">
            {t("cart.configuration.addons.error")}
          </p>
        )}

        {!(isPending || isError) && hasAddons && data && (
          <DietAddonsCarousel
            cateringName={cateringName}
            categories={data}
            onAddAddonAction={setSelectedAddon}
          />
        )}
      </div>

      {selectedAddon && (
        <DietAddonModal
          addon={selectedAddon}
          cateringName={cateringName}
          categoryName={selectedAddonCategoryName}
          dietDates={dietDates ?? []}
          onCommitted={() => {
            // Don't just close: hand off to the summary dialog so the user sees
            // the addon they just added alongside the rest of the selection.
            setSelectedAddon(null);
            setSummaryOpen(true);
          }}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedAddon(null);
            }
          }}
          open={Boolean(selectedAddon)}
          subItemsPricing={subItemsPricing}
        />
      )}

      {data && (
        <DietAddonsSummaryDialog
          cateringName={cateringName}
          categories={data}
          dietDates={dietDates ?? []}
          onOpenChange={setSummaryOpen}
          open={summaryOpen}
          subItemsPricing={subItemsPricing}
        />
      )}
    </section>
  );
};
